import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Mail, CheckCircle2 } from 'lucide-react';
import PhoneField from '../PhoneField';

/**
 * SavePreviewModal
 *
 * The "Email me this view" dialog of the ViewOnWall composer. Lifted out
 * of `ViewOnWall.jsx` (which was approaching 1,000 lines) and exposed as
 * a controlled component. Parent owns the form state and submit handler
 * so behaviour is identical to before.
 */
const SavePreviewModal = ({
  open,
  onClose,
  artwork,
  emailForm,
  setEmailForm,
  emailStatus,
  onSubmit,
}) => (
  <AnimatePresence>
    {open && (
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-[10000] bg-black/80 backdrop-blur-md flex items-center justify-center p-4"
        onClick={() => !emailStatus.sending && onClose()}
      >
        <motion.div
          initial={{ scale: 0.96, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.96, opacity: 0 }}
          className="bg-white max-w-md w-full rounded-2xl p-7 sm:p-8 relative"
          onClick={(e) => e.stopPropagation()}
          data-testid="vow-email-modal"
        >
          <button
            onClick={() => !emailStatus.sending && onClose()}
            className="absolute top-4 right-4 text-neutral-400 hover:text-neutral-700 transition-colors"
            aria-label="Close"
          >
            <X className="w-5 h-5" />
          </button>

          {emailStatus.sent ? (
            <div className="text-center py-6">
              <CheckCircle2 className="w-14 h-14 text-emerald-500 mx-auto mb-4" />
              <h3 className="font-serif text-2xl text-neutral-900 mb-2">Preview sent</h3>
              <p className="text-neutral-600 text-sm leading-relaxed mb-6">
                Your wall preview is on its way to{' '}
                <strong>{emailForm.email}</strong>. Gabrielle's team has
                been notified and will follow up if you'd like to discuss
                the work.
              </p>
              <button
                onClick={onClose}
                className="px-6 py-2 bg-black text-white text-sm uppercase tracking-wider hover:bg-neutral-800 transition-colors"
                data-testid="vow-email-close-success-btn"
              >
                Close
              </button>
            </div>
          ) : (
            <>
              <p className="text-[10px] uppercase tracking-[0.3em] text-neutral-500 mb-3">
                Save this preview
              </p>
              <h3 className="font-serif text-2xl text-neutral-900 mb-2">
                Email me this view
              </h3>
              <p className="text-sm text-neutral-600 leading-relaxed mb-6">
                We'll send the composed image of <strong>{artwork.title}</strong> on
                your wall — no spam, just the picture and the details.
              </p>

              <form onSubmit={onSubmit} className="space-y-3">
                <input
                  type="text"
                  required
                  placeholder="Your name"
                  value={emailForm.name}
                  onChange={(e) => setEmailForm({ ...emailForm, name: e.target.value })}
                  className="w-full px-4 py-3 bg-neutral-50 border border-neutral-200 rounded-lg text-sm focus:outline-none focus:border-neutral-900 transition-colors"
                  data-testid="vow-email-name-input"
                />
                <input
                  type="email"
                  required
                  placeholder="Email address"
                  value={emailForm.email}
                  onChange={(e) => setEmailForm({ ...emailForm, email: e.target.value })}
                  className="w-full px-4 py-3 bg-neutral-50 border border-neutral-200 rounded-lg text-sm focus:outline-none focus:border-neutral-900 transition-colors"
                  data-testid="vow-email-address-input"
                />
                <PhoneField
                  value={emailForm.phone}
                  onChange={(v) => setEmailForm({ ...emailForm, phone: v || '' })}
                  required
                  placeholder="Phone number"
                  testIdPrefix="vow-email-phone"
                />
                <textarea
                  placeholder="Optional note for Gabrielle (room, framing thoughts, questions…)"
                  rows={3}
                  value={emailForm.message}
                  onChange={(e) => setEmailForm({ ...emailForm, message: e.target.value })}
                  className="w-full px-4 py-3 bg-neutral-50 border border-neutral-200 rounded-lg text-sm focus:outline-none focus:border-neutral-900 transition-colors resize-none"
                  data-testid="vow-email-message-input"
                />

                {emailStatus.error && (
                  <p className="text-sm text-red-600" data-testid="vow-email-error">
                    {emailStatus.error}
                  </p>
                )}

                <button
                  type="submit"
                  disabled={emailStatus.sending}
                  className="w-full px-6 py-3 bg-black text-white text-sm uppercase tracking-wider hover:bg-neutral-800 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                  data-testid="vow-email-send-btn"
                >
                  {emailStatus.sending ? (
                    <>Composing & sending…</>
                  ) : (
                    <>
                      <Mail className="w-4 h-4" />
                      Send me this preview
                    </>
                  )}
                </button>
                <p className="text-[11px] text-neutral-500 text-center mt-2">
                  A copy goes to Gabrielle's team so she can follow up if helpful.
                </p>
              </form>
            </>
          )}
        </motion.div>
      </motion.div>
    )}
  </AnimatePresence>
);

export default SavePreviewModal;
